import { supabase } from '@/lib/supabase';

// ── Types ──

/**
 * Why the Wallet API rejected a valid token with 403: the Wallet account has
 * no active Premium and/or API access is switched off in Wallet settings.
 */
export type WalletEntitlementStatus =
  | 'PREMIUM_REQUIRED'
  | 'API_CONSENT_REQUIRED'
  | 'API_CONSENT_AND_PREMIUM_REQUIRED';

export interface WalletConnection {
  id: string;
  user_id: string;
  is_active: boolean;
  last_sync_at: string | null;
  last_sync_error: string | null;
  /** Set (and the connection paused) while the Wallet account is not entitled to the API. */
  entitlement_status: WalletEntitlementStatus | null;
  created_at: string;
  updated_at: string;
}

export interface WalletAccount {
  id: string;
  name: string;
  currencyCode: string;
  accountType: string;
}

export interface WalletAccountMapping {
  id: string;
  user_id: string;
  connection_id: string;
  wallet_account_id: string;
  wallet_account_name: string | null;
  wallet_currency: string | null;
  group_id: string;
  is_active: boolean;
  auto_approve: boolean;
  created_at: string;
  updated_at: string;
  group?: { id: string; name: string; currency_code: string; color: string | null };
}

export interface WalletTransaction {
  id: string;
  user_id: string;
  connection_id: string;
  mapping_id: string;
  group_id: string;
  wallet_transaction_id: string;
  wallet_account_id: string;
  wallet_category_name: string | null;
  wallet_category_envelope_id: number | null;
  wallet_category_id: string | null;
  wallet_payee: string | null;
  description: string;
  amount_minor_src: number;
  currency_code_src: string;
  transaction_date: string;
  note: string | null;
  status: 'pending' | 'approved' | 'dismissed' | 'auto_approved';
  expense_id: string | null;
  created_at: string;
  processed_at: string | null;
  raw_data?: { recordState?: string; [key: string]: unknown };
  group?: { id: string; name: string; color: string | null };
  mapping?: { wallet_account_name: string | null };
}

// ── Edge Function calls ──

async function invokeEdgeFunction<T = Record<string, unknown>>(
  functionName: string,
  body: Record<string, unknown>,
): Promise<T> {
  const { data, error } = await supabase.functions.invoke(functionName, {
    body,
  });

  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data as T;
}

export async function connectWallet(walletApiToken: string): Promise<{
  accounts: WalletAccount[];
  sync_in_progress?: boolean;
  entitlement_status?: WalletEntitlementStatus | null;
}> {
  return invokeEdgeFunction('wallet-manage-connection', {
    action: 'connect',
    wallet_api_token: walletApiToken,
  });
}

export async function disconnectWallet(): Promise<void> {
  await invokeEdgeFunction('wallet-manage-connection', { action: 'disconnect' });
}

export async function testWalletConnection(): Promise<{
  valid: boolean;
  error?: string;
  entitlement_status?: WalletEntitlementStatus | null;
}> {
  return invokeEdgeFunction('wallet-manage-connection', { action: 'test' });
}

export interface WalletAccountsResponse {
  accounts: WalletAccount[];
  /** Wallet is still syncing the user's data into its API; a just-created account may not be listed yet. */
  sync_in_progress?: boolean;
}

export async function listWalletAccounts(): Promise<WalletAccountsResponse> {
  return invokeEdgeFunction('wallet-manage-connection', { action: 'list-accounts' });
}

export interface WalletSyncResult {
  inserted: number;
  updated: number;
  skipped: number;
  auto_approved: number;
  synced: number;
  errors: string[];
  /** Wallet was still syncing its API data; records created moments ago may arrive on the next sync. */
  sync_in_progress?: boolean;
}

export async function triggerWalletSync(): Promise<WalletSyncResult> {
  return invokeEdgeFunction<WalletSyncResult>('sync-wallet-transactions', { trigger: 'manual' });
}

export async function approveTransactions(
  transactionIds: string[],
  overrides?: { description?: string; category_id?: string },
): Promise<{ results: { id: string; status: string; expense_id?: string; error?: string }[] }> {
  return invokeEdgeFunction('wallet-approve-transactions', {
    action: 'approve',
    transaction_ids: transactionIds,
    overrides,
  });
}

export async function dismissTransactions(
  transactionIds: string[],
): Promise<{ results: { id: string; status: string }[] }> {
  return invokeEdgeFunction('wallet-approve-transactions', {
    action: 'dismiss',
    transaction_ids: transactionIds,
  });
}

// ── Direct Supabase queries ──

export async function fetchWalletConnection(userId: string): Promise<WalletConnection | null> {
  const { data, error } = await supabase
    .from('wallet_connections')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function fetchWalletMappings(userId: string): Promise<WalletAccountMapping[]> {
  const { data, error } = await supabase
    .from('wallet_account_mappings')
    .select(`
      *,
      group:groups(id, name, currency_code, color)
    `)
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data ?? []) as WalletAccountMapping[];
}

export async function createWalletMapping(mapping: {
  connectionId: string;
  walletAccountId: string;
  walletAccountName: string;
  walletCurrency: string;
  groupId: string;
  userId: string;
}): Promise<string> {
  const id = crypto.randomUUID();
  const { error } = await supabase
    .from('wallet_account_mappings')
    .insert({
      id,
      user_id: mapping.userId,
      connection_id: mapping.connectionId,
      wallet_account_id: mapping.walletAccountId,
      wallet_account_name: mapping.walletAccountName,
      wallet_currency: mapping.walletCurrency,
      group_id: mapping.groupId,
    });

  if (error) throw error;
  return id;
}

export async function updateWalletMapping(
  mappingId: string,
  updates: { group_id?: string; is_active?: boolean; auto_approve?: boolean },
): Promise<void> {
  const { error } = await supabase
    .from('wallet_account_mappings')
    .update(updates)
    .eq('id', mappingId);

  if (error) throw error;
}

export async function deleteWalletMapping(mappingId: string): Promise<void> {
  const { error } = await supabase
    .from('wallet_account_mappings')
    .delete()
    .eq('id', mappingId);

  if (error) throw error;
}

export async function fetchPendingTransactions(userId: string): Promise<WalletTransaction[]> {
  const { data, error } = await supabase
    .from('wallet_transactions')
    .select(`
      *,
      group:groups(id, name, color),
      mapping:wallet_account_mappings!mapping_id(wallet_account_name)
    `)
    .eq('user_id', userId)
    .eq('status', 'pending')
    .order('transaction_date', { ascending: false });

  if (error) throw error;
  return (data ?? []) as WalletTransaction[];
}

export async function fetchAllTransactions(
  userId: string,
  status?: string,
): Promise<WalletTransaction[]> {
  let query = supabase
    .from('wallet_transactions')
    .select(`
      *,
      group:groups(id, name, color),
      mapping:wallet_account_mappings!mapping_id(wallet_account_name)
    `)
    .eq('user_id', userId)
    .order('transaction_date', { ascending: false })
    .limit(100);

  if (status) {
    query = query.eq('status', status);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as WalletTransaction[];
}

export async function fetchPendingTransactionCount(userId: string): Promise<number> {
  const { count, error } = await supabase
    .from('wallet_transactions')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('status', 'pending');

  if (error) throw error;
  return count ?? 0;
}
